import { useState } from "react";
import {
  Table,
  Tag,
  Popconfirm,
  Switch,
  Pagination,
  Button,
  Tooltip,
  Card,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { IoTrashOutline, IoKeyOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { BiEdit } from "react-icons/bi";
import { BsArrowUpCircle } from "react-icons/bs";
import { MdHistory } from "react-icons/md";
import { SiMicrosoftexcel } from "react-icons/si";
import { Col, Row } from "react-bootstrap";
import moment from "moment";
import CreateModal from "./createModal";
import UpdateModal from "./updateModal";
import UpgradeModal from "./upgradeModal";
import SubscriptionHistory from "./subscriptionHistory";
import { useSelector } from "react-redux";
import { formatMoney, getCountry } from "../../../config/countries";
import { useEpaperConfig } from "../../../config/epaperConfig";

interface EpaperUser {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  status: "active" | "inactive" | "suspended";
  email_verified: boolean;
  last_login: string | null;
  createdAt: string;
  role?: string | null;
  register_mode?: string;
  amount?: string | null;
  currency?: string | null;
  country?: string | null;
  zone?: string | null;
  zone_code?: string | null;
  subscriber_code?: string | null;
  subscription_start_date?: string | null;
  subscription_end_date?: string | null;
  agent_code?: string | null;
  agent_name?: string | null;
  current_plan_id?: number | null;
  current_plan_type?: string | null;
  subscription_status?:
    | "active"
    | "expired"
    | "cancelled"
    | "paused"
    | "pending"
    | "free"
    | "none"
    | null;
  latest_subscription_id?: number | null;
  last_paid_amount?: number | null;
  created_by?: {
    id?: number;
    username?: string;
    email?: string;
    code?: string | null;
    role?: { id: number; name: string } | null;
  } | null;
}

interface DataTableProps {
  data: EpaperUser[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize: number) => void;
  onEdit?: (user: EpaperUser) => void;
  onDelete?: (userId: number) => void;
  onToggleStatus?: (userId: number, newStatus: string) => void;
  onResetPassword?: (user: EpaperUser) => void;
  reload?: () => void;
  onExport?: () => void;
  exportLoading?: boolean;
}

function DataTable({
  data,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onEdit,
  onDelete,
  onToggleStatus,
  onResetPassword,
  onExport,
  exportLoading = false,
  reload,
}: DataTableProps) {
  // Country names/flags come from E-Paper Settings.
  useEpaperConfig();
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const navigate = useNavigate();
  const [createModal, setCreateModal] = useState(false);
  const [editUser, setEditUser] = useState<boolean>(false);
  const [upgradeUser, setUpgradeUser] = useState<boolean>(false);
  const [historyUser, setHistoryUser] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<EpaperUser | null>(null);

  const handleChange = (pagination: any, filters: any, sorter: any) => {
    setSortedInfo(sorter);
  };
  const User = useSelector((state: any) => state.User.user);

  const handleSwitchChange = (checked: boolean, record: EpaperUser) => {
    const newStatus = checked ? "active" : "inactive";
    onToggleStatus?.(record.id, newStatus);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "green";
      case "inactive":
        return "orange";
      case "suspended":
        return "red";
      default:
        return "default";
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: ColumnsType<EpaperUser> = [
    {
      title: "S.No",
      key: "serial",
      width: 60,
      render: (text, record, index) => {
        return <div>{(page - 1) * pageSize + index + 1}</div>;
      },
    },
    {
      title: "Code",
      dataIndex: "subscriber_code",
      key: "subscriber_code",
      width: 80,
      render: (text: string) => (
        <span
          style={{
            fontFamily: "monospace",
            fontWeight: 600,
            color: "#0576b7",
          }}
        >
          {text || "-"}
        </span>
      ),
    },
    {
      title: "Full Name",
      dataIndex: "full_name",
      key: "full_name",
      width: 200,
      sorter: (a: EpaperUser, b: EpaperUser) =>
        a.full_name.localeCompare(b.full_name),
      sortOrder: sortedInfo.columnKey === "full_name" && sortedInfo.order,
      render: (text: string, record: EpaperUser) => {
        return (
          <div
            className="cursor-pointer hover:text-blue-600"
            onClick={() => onEdit?.(record)}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 120,
      render: (text: string, record: EpaperUser) => {
        return (
          <div className="flex flex-col">
            <Tag
              color={text ? "green" : "orange"}
              style={{
                textAlign: "center",
                textTransform: "capitalize",
              }}
            >
              {text ?? "User"}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 250,
      render: (text: string, record: EpaperUser) => {
        return (
          <div className="flex flex-col">
            <span className="text-sm">{text}</span>
          </div>
        );
      },
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 150,
      render: (text: string) => {
        return <span className="text-sm font-mono">{text}</span>;
      },
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      width: 170,
      // Records created before the country field existed are UAE.
      render: (country: string | null) => {
        const c = getCountry(country);
        return (
          <span className="text-sm">
            {c.name}
          </span>
        );
      },
    },
    {
      title: "Zone",
      dataIndex: "zone",
      key: "zone",
      width: 150,
      render: (text: string, record: EpaperUser) => {
        return (
          <span className="text-sm">
            {text || "N/A"}
            {text && record.zone_code ? ` (${record.zone_code})` : ""}
          </span>
        );
      },
    },
    {
      title: "Register Mode",
      dataIndex: "register_mode",
      key: "register_mode",
      width: 120,
      render: (register_mode: string) => {
        return (
          <Tag
            color={register_mode === "agent" ? "Blue" : "Green"}
            style={{
              width: 80,
              textAlign: "center",
              textTransform: "capitalize",
            }}
          >
            {register_mode === "agent"
              ? `Agent`
              : register_mode === "paytabs"
                ? "Paytabs"
                : "Signup"}
          </Tag>
        );
      },
    },
    {
      title: "Agent",
      key: "agent",
      width: 200,
      render: (_: any, record: EpaperUser) => {
        const name =
          record.agent_name ||
          record.created_by?.username ||
          null;
        const code =
          record.agent_code ||
          record.created_by?.code ||
          null;
        if (!name && !code) {
          return <span style={{ color: "#999" }}>—</span>;
        }
        return (
          <div>
            {name && <div>{name}</div>}
            {code && (
              <div
                style={{
                  fontSize: 11,
                  color: "#0576b7",
                }}
              >
                {code}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Registered By",
      key: "created_by",
      width: 180,
      render: (_: any, record: EpaperUser) => {
        const username = record.created_by?.username || null;
        if (username) {
          const role =
            record.created_by?.role?.id === 4
              ? "Admin"
              : record.created_by?.role?.id === 5
                ? "Agent"
                : record.created_by?.role?.id === 6
                  ? "E-Paper Admin"
                  : record.created_by?.role?.name || null;
          return (
            <div>
              <div>{username}</div>
              {role && (
                <div style={{ fontSize: 11, color: "#888" }}>{role}</div>
              )}
            </div>
          );
        }
        // No registrar on file — self-signup if the register mode says so.
        const mode = record.register_mode;
        if (!mode || mode !== "agent") {
          return (
            <Tag color="purple" style={{ textAlign: "center" }}>
              Self
            </Tag>
          );
        }
        return <span style={{ color: "#999" }}>—</span>;
      },
    },
    {
      title: "Registration Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      sorter: (a: EpaperUser, b: EpaperUser) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      sortOrder: sortedInfo.columnKey === "createdAt" && sortedInfo.order,
      render: (date: string) => {
        return (
          <Tooltip title={formatDateTime(date)}>
            {/* <span className="text-xs text-gray-600">{date}</span> */}
            <span className="text-xs text-gray-600">
              {moment(date).format("DD-MM-YYYY hh:mm A")}
            </span>
            {/* <span className="text-xs text-gray-600">{formatDate(date)}</span> */}
          </Tooltip>
        );
      },
    },

    {
      title: "Payment Status",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (amount: string, record: EpaperUser) => {
        return (
          <Tag
            color={amount ? "Red" : "Indigo"}
            style={{
              minWidth: 80,
              textAlign: "center",
            }}
          >
            {amount ? `Paid ${formatMoney(amount, record.currency)}` : "Free"}
          </Tag>
        );
      },
    },
    {
      title: "Subscription End",
      dataIndex: "subscription_end_date",
      key: "subscription_end_date",
      width: 150,
      sorter: (a: EpaperUser, b: EpaperUser) => {
        const av = a.subscription_end_date
          ? new Date(a.subscription_end_date).getTime()
          : 0;
        const bv = b.subscription_end_date
          ? new Date(b.subscription_end_date).getTime()
          : 0;
        return av - bv;
      },
      sortOrder:
        sortedInfo.columnKey === "subscription_end_date" && sortedInfo.order,
      render: (date: string | null | undefined, record: EpaperUser) => {
        if (!date) {
          return <span style={{ color: "#999" }}>—</span>;
        }
        const end = moment(date);
        const now = moment();
        const dateExpired = end.isBefore(now);
        const daysLeft = end.diff(now, "days");

        // Status flag wins over the date when it explicitly says cancelled,
        // refunded, paused, etc. — the date can still be in the future after
        // a cancel/refund, but the subscription isn't really "active" anymore.
        const status = (record.subscription_status || "").toLowerCase();
        const statusOverride =
          status === "cancelled" || status === "refunded"
            ? "Cancelled"
            : status === "paused"
              ? "Paused"
              : status === "expired"
                ? "Expired"
                : null;
        const overrideColor =
          statusOverride === "Cancelled"
            ? "#cf1322"
            : statusOverride === "Paused"
              ? "#fa8c16"
              : statusOverride === "Expired"
                ? "#cf1322"
                : null;

        const labelText = statusOverride
          ? statusOverride
          : dateExpired
            ? `Expired ${end.fromNow()}`
            : daysLeft <= 7
              ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`
              : `Active`;
        const labelColor = overrideColor || (dateExpired ? "#cf1322" : "#52c41a");
        const dateColor =
          overrideColor || (dateExpired ? "#cf1322" : "#222");

        return (
          <Tooltip title={end.format("DD MMM YYYY, hh:mm A")}>
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: dateColor,
                  textDecoration: statusOverride === "Cancelled" ? "line-through" : "none",
                }}
              >
                {end.format("DD-MM-YYYY")}
              </div>
              <div style={{ fontSize: 11, color: labelColor }}>
                {labelText}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: "right" as const,
      render: (_, record: EpaperUser) => {
        return (
          <div>
            <Tooltip title="Reset Password">
              <IoKeyOutline
                size={18}
                color="#1890ff"
                style={{ cursor: "pointer", marginRight: "12px" }}
                onClick={() => onResetPassword?.(record)}
              />
            </Tooltip>

            <Tooltip title={record.amount ? "Renew Subscription" : "Upgrade to Premium"}>
              <BsArrowUpCircle
                size={16}
                color="#52c41a"
                style={{ cursor: "pointer", marginRight: "12px" }}
                onClick={() => {
                  setSelectedUser(record);
                  setUpgradeUser(true);
                }}
              />
            </Tooltip>

            <Tooltip title="Subscription History">
              <MdHistory
                size={18}
                color="#722ed1"
                style={{ cursor: "pointer", marginRight: "12px" }}
                onClick={() => {
                  setSelectedUser(record);
                  setHistoryUser(true);
                }}
              />
            </Tooltip>

            <Tooltip title="Edit User">
              <BiEdit
                size={18}
                color="#333333ff"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectedUser(record);
                  setEditUser(true);
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    // {
    //   title: "Active",
    //   key: "active_toggle",
    //   width: 80,
    //   render: (text, record: EpaperUser) => {
    //     return (
    //       <Switch
    //         checked={record.status === "active"}
    //         onChange={(checked) => handleSwitchChange(checked, record)}
    //         size="small"
    //       />
    //     );
    //   },
    // },
  ];

  return (
    <div className="mt-4">
      {/* Action row: on mobile stacks as [row-count over full width]
          with Export + Create sitting on a second row sharing the width
          equally (xs={6} each). On desktop the layout is preserved. */}
      <Row className="g-2">
        <Col xs={12} md={8}>
          <div className="text-sm text-gray-600">
            Showing {(page - 1) * pageSize + 1} to{" "}
            {Math.min(page * pageSize, total)} of {total} users
          </div>
        </Col>

        <Col xs={6} md={2}>
          <Button
            style={{ width: "100%" }}
            type="primary"
            size="large"
            icon={<SiMicrosoftexcel />}
            onClick={onExport}
            loading={exportLoading}
            className="bg-green-600 hover:bg-green-700 border-green-600"
          >
            Export
          </Button>
        </Col>

        <Col xs={6} md={2}>
          <Button
            style={{ width: "100%" }}
            type="primary"
            size="large"
            onClick={() => setCreateModal(true)}
            disabled={exportLoading}
            className="bg-green-600 hover:bg-green-700 border-green-600"
          >
            Create
          </Button>
        </Col>

        {/* <Col md={1}>
          <Button
            icon={<ReloadOutlined />}
            style={{ width: "100%" }}
            type="default"
            size="large"
            onClick={reload}
            loading={loading}
            className="bg-green-600 hover:bg-green-700 border-green-600"
          ></Button>
        </Col> */}
      </Row>
      <br />

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
        onChange={handleChange}
        size="small"
        rowKey="id"
        scroll={{ x: "max-content" }}
        className="border border-gray-200 rounded-lg"
      />

      <div className="flex justify-end mt-4">
        <Pagination
          current={page}
          total={total}
          pageSize={pageSize}
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} of ${total} users`
          }
          onChange={onPageChange}
          pageSizeOptions={["10", "20", "50", "100"]}
          className="text-sm"
        />
      </div>

      {createModal ? (
        <CreateModal
          open={createModal}
          refresh={reload || (() => { })}
          close={() => {
            setCreateModal(false);
            setSelectedUser(null);
          }}
          onUpgradeExisting={(existing: any) => {
            // Hand-off from create flow when identity check finds an
            // existing subscriber. Close create, stage the existing user,
            // and open the real UpgradeModal so the admin completes the
            // subscription through the proper path.
            setCreateModal(false);
            setSelectedUser(existing as EpaperUser);
            setUpgradeUser(true);
          }}
        />
      ) : null}

      {editUser && selectedUser ? (
        <UpdateModal
          open={editUser}
          refresh={reload || (() => { })}
          close={() => {
            setEditUser(false);
            setSelectedUser(null);
          }}
          userData={selectedUser}
        />
      ) : null}

      {upgradeUser && selectedUser ? (
        <UpgradeModal
          open={upgradeUser}
          refresh={reload || (() => { })}
          close={() => {
            setUpgradeUser(false);
            setSelectedUser(null);
          }}
          userData={selectedUser}
        />
      ) : null}

      {historyUser && selectedUser ? (
        <SubscriptionHistory
          open={historyUser}
          close={() => {
            setHistoryUser(false);
            setSelectedUser(null);
          }}
          userData={selectedUser}
          onChanged={reload}
        />
      ) : null}
    </div>
  );
}

export default DataTable;
